/**
 * Task 10.4: Performance monitoring utility (Requirements: 12.1, 12.2, 12.3, 12.4, 12.5)
 * 
 * This utility provides performance monitoring for map interactions:
 * - Logs timing for marker clicks
 * - Logs timing for zoom/pan operations
 * - Logs timing for table synchronization
 * - Identifies operations exceeding performance targets
 * 
 * Performance targets:
 * - Marker click response: < 50ms
 * - Zoom animation: 350ms
 * - Pan animation: 200ms
 * - Table sync: < 100ms
 * - Popup open: < 50ms after animation
 * - Fit bounds: < 1000ms
 */

export interface PerformanceMetrics {
  operation: string;
  duration: number;
  timestamp: number;
  exceedsTarget: boolean;
  target: number;
}

// Performance targets in milliseconds
export const PERFORMANCE_TARGETS = {
  MARKER_CLICK: 50,
  ZOOM_ANIMATION: 350,
  PAN_ANIMATION: 200,
  TABLE_SYNC: 100,
  POPUP_OPEN: 50,
  FIT_BOUNDS: 1000,
  CLOSE_POPUPS: 50,
} as const;

class PerformanceMonitor {
  private metrics: PerformanceMetrics[] = [];
  private timers: Map<string, number> = new Map();
  private maxMetrics = 100; // Keep last 100 metrics

  /**
   * Start timing an operation
   * @param operationId - Unique identifier for the operation
   */
  startTimer(operationId: string): void {
    this.timers.set(operationId, performance.now());
  }

  /**
   * End timing an operation and log the result
   * @param operationId - Unique identifier for the operation
   * @param operation - Human-readable operation name
   * @param target - Performance target in milliseconds
   */
  endTimer(operationId: string, operation: string, target: number): PerformanceMetrics | null {
    const startTime = this.timers.get(operationId);
    
    if (!startTime) {
      console.warn(`[PerformanceMonitor] No start time found for operation: ${operationId}`);
      return null;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    const exceedsTarget = duration > target;
    
    const metric: PerformanceMetrics = {
      operation,
      duration,
      timestamp: Date.now(),
      exceedsTarget,
      target,
    };
    
    // Add to metrics array
    this.metrics.push(metric);
    
    // Keep only last maxMetrics entries
    if (this.metrics.length > this.maxMetrics) {
      this.metrics.shift();
    }
    
    // Clean up timer
    this.timers.delete(operationId);
    
    // Log result
    if (exceedsTarget) {
      console.warn(
        `⚠️ [Performance] ${operation} took ${duration.toFixed(2)}ms (target: ${target}ms, exceeded by ${(duration - target).toFixed(2)}ms)`
      );
    } else {
      console.log(
        `✓ [Performance] ${operation} took ${duration.toFixed(2)}ms (target: ${target}ms)`
      );
    }
    
    return metric;
  }

  /**
   * Get all recorded metrics
   */
  getMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  /**
   * Get metrics that exceeded their targets
   */
  getSlowOperations(): PerformanceMetrics[] {
    return this.metrics.filter(m => m.exceedsTarget);
  }

  /**
   * Get average duration for a specific operation type
   */
  getAverageDuration(operation: string): number | null {
    const operationMetrics = this.metrics.filter(m => m.operation === operation);
    
    if (operationMetrics.length === 0) {
      return null;
    }
    
    const total = operationMetrics.reduce((sum, m) => sum + m.duration, 0);
    return total / operationMetrics.length;
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics = [];
    this.timers.clear();
  }

  /**
   * Get performance summary
   */
  getSummary(): {
    totalOperations: number;
    slowOperations: number;
    averageDurations: Record<string, number>;
  } {
    const slowOps = this.getSlowOperations();
    
    // Calculate average durations by operation type
    const operationTypes = [...new Set(this.metrics.map(m => m.operation))];
    const averageDurations: Record<string, number> = {};
    
    operationTypes.forEach(op => {
      const avg = this.getAverageDuration(op);
      if (avg !== null) {
        averageDurations[op] = avg;
      }
    });
    
    return {
      totalOperations: this.metrics.length,
      slowOperations: slowOps.length,
      averageDurations,
    };
  }

  /**
   * Log performance summary to console
   */
  logSummary(): void {
    const summary = this.getSummary();
    
    console.group('📊 Performance Summary');
    console.log(`Total operations: ${summary.totalOperations}`);
    console.log(`Slow operations: ${summary.slowOperations} (${((summary.slowOperations / summary.totalOperations) * 100).toFixed(1)}%)`);
    console.log('Average durations:');
    
    Object.entries(summary.averageDurations).forEach(([op, avg]) => {
      console.log(`  ${op}: ${avg.toFixed(2)}ms`);
    });
    
    console.groupEnd();
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Export helper functions for common operations
export const monitorMarkerClick = (stationCode: string) => {
  performanceMonitor.startTimer(`marker-click-${stationCode}`);
};

export const endMarkerClick = (stationCode: string) => {
  return performanceMonitor.endTimer(
    `marker-click-${stationCode}`,
    'Marker Click',
    PERFORMANCE_TARGETS.MARKER_CLICK
  );
};

export const monitorZoomAnimation = (stationCode: string) => {
  performanceMonitor.startTimer(`zoom-${stationCode}`);
};

export const endZoomAnimation = (stationCode: string) => {
  return performanceMonitor.endTimer(
    `zoom-${stationCode}`,
    'Zoom Animation',
    PERFORMANCE_TARGETS.ZOOM_ANIMATION
  );
};

export const monitorPanAnimation = (stationCode: string) => {
  performanceMonitor.startTimer(`pan-${stationCode}`);
};

export const endPanAnimation = (stationCode: string) => {
  return performanceMonitor.endTimer(
    `pan-${stationCode}`,
    'Pan Animation',
    PERFORMANCE_TARGETS.PAN_ANIMATION
  );
};

export const monitorTableSync = (stationCode: string) => {
  performanceMonitor.startTimer(`table-sync-${stationCode}`);
};

export const endTableSync = (stationCode: string) => {
  return performanceMonitor.endTimer(
    `table-sync-${stationCode}`,
    'Table Synchronization',
    PERFORMANCE_TARGETS.TABLE_SYNC
  );
};

export const monitorPopupOpen = (stationCode: string) => {
  performanceMonitor.startTimer(`popup-${stationCode}`);
};

export const endPopupOpen = (stationCode: string) => {
  return performanceMonitor.endTimer(
    `popup-${stationCode}`,
    'Popup Open',
    PERFORMANCE_TARGETS.POPUP_OPEN
  );
};

export const monitorClosePopups = () => {
  performanceMonitor.startTimer('close-popups');
};

export const endClosePopups = () => {
  return performanceMonitor.endTimer(
    'close-popups',
    'Close All Popups',
    PERFORMANCE_TARGETS.CLOSE_POPUPS
  );
};

export const monitorFitBounds = (stationCount: number) => {
  performanceMonitor.startTimer(`fit-bounds-${stationCount}`);
};

export const endFitBounds = (stationCount: number) => {
  return performanceMonitor.endTimer(
    `fit-bounds-${stationCount}`,
    `Fit Bounds (${stationCount} stations)`,
    PERFORMANCE_TARGETS.FIT_BOUNDS
  );
};
